import { useDrawerStatus } from '@react-navigation/drawer'
import { usePathname, useRouter } from 'expo-router'
import { useEffect, useRef, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Platform, ScrollView, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { env } from '@/src/config/env'
import { AccessibilityHiddenTitle } from '@/src/features/accessibility/ui/components/AccessibilityHiddenTitle'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { getIsLoggedIn } from '@/src/features/auth/utils/getIsLoggedIn'
import { DONATION_SOURCE } from '@/src/features/donation/api/types/donation'
import { useDonationContext } from '@/src/features/donation/context/DonationContext'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { getLegacyUrl } from '@/src/general/utils/getLegacyUrl'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { Button } from '../button/default'
import { MenuItem } from './menuItem'
import { MenuLocaleRestricted } from './MenuLocaleRestricted'

export type TMenuProps = {
  onClose?: VoidFunction
}

export const Menu = ({ onClose }: TMenuProps): ReactNode => {
  const menuRef = useRef<View>(null)
  const scrollViewRef = useRef<ScrollView>(null)
  const { top, bottom } = useSafeAreaInsets()
  const isDrawerOpen = useDrawerStatus() === 'open'

  useEffect(() => {
    if (isDrawerOpen && menuRef.current && Platform.OS === 'web') {
      // @ts-expect-error - removeAttribute is not typed for views
      if (typeof scrollViewRef.current?.removeAttribute === 'function') {
        // @ts-expect-error - removeAttribute is not typed for views
        scrollViewRef.current?.removeAttribute('inert')
      }
      menuRef.current.focus()
    } else if (!isDrawerOpen && scrollViewRef.current && Platform.OS === 'web') {
      // @ts-expect-error - setAttribute is not typed for views
      scrollViewRef.current.setAttribute('inert', true)
    }
  }, [isDrawerOpen])

  const { t } = useTranslate()
  const { locale } = useIntl()
  const { routes } = useRoutes()
  const { navigate } = useRouter()
  const pathname = usePathname()
  const { openDonationBanner } = useDonationContext()
  const { data: me } = useMeQuery()

  const isLoggedIn = getIsLoggedIn()
  const showConfessionItem = isLoggedIn ? me?.countryCode === 'FR' : locale === 'fr'

  const connectUrl = routes.connect(`${env.EXPO_PUBLIC_WEB_BASE_URL}${pathname}`)

  const liveItem = { children: t('navigation.live-page-link'), href: getLegacyUrl('live'), icon: ICON.VIDEO_RECORDER }

  return (
    <ScrollView
      ref={scrollViewRef}
      style={{ marginTop: top, marginBottom: bottom }}
      showsVerticalScrollIndicator={false}
      contentContainerClassName="flex h-auto max-w-full flex-col px-4 pt-18 bg-white"
    >
      <AccessibilityHiddenTitle>
        <FormattedMessage id="app.navigation.open-menu" />
      </AccessibilityHiddenTitle>
      <Button
        isIconOnly
        startIcon={ICON.CLOSE}
        colors="default"
        variant="light"
        contrast="low"
        className="absolute right-4 top-4"
        onPress={onClose}
        tabIndex={0}
        ref={menuRef}
      />

      {!!isLoggedIn && (
        <>
          <MenuItem.Alert icon={ICON.STARS} navigateTo={routes.preferences()}>
            {t('user.wizard.my-wizard')}
          </MenuItem.Alert>
          <MenuItem.Listbox
            bold
            items={[
              {
                children: t('settings.profile-and-settings'),
                href: routes.settings(),
                icon: ICON.PROFILE_CIRCLE,
                testID: TEST_ID.NAVIGATION.SETTINGS_LINK,
              },
              ...(me && me.communitiesCount > 0
                ? [
                    {
                      children: t('community.toolbar.title'),
                      href: routes.animatorSpace(),
                      icon: ICON.USER_EDIT,
                    },
                  ]
                : []),
            ]}
          />
        </>
      )}
      {!isLoggedIn && (
        <>
          <View className="flex w-full flex-row gap-2 py-4">
            <Button colors="primary" variant="solid" shadow="sm" className="w-1/2" onPress={() => navigate(connectUrl)}>
              {t('auth.signup.signup')}
            </Button>
            <Button colors="primary" variant="faded" className="w-1/2" onPress={() => navigate(connectUrl)}>
              {t('auth.login.login')}
            </Button>
          </View>
          <MenuItem.Listbox
            bold
            items={[
              { children: t('navigation.search'), href: routes.communitiesSearch(), icon: ICON.SEARCH },
              {
                children: t('navigation.intentions'),
                href: routes.intentionsForm(),
                icon: ICON.HOZANA_INTENTION,
              },
            ]}
          />
        </>
      )}
      {Platform.OS === 'web' && (
        <MenuItem.Alert icon={ICON.PHONE} navigateTo={routes.app()}>
          {t('home.welcome.app-section.title')}
        </MenuItem.Alert>
      )}
      <MenuLocaleRestricted locale="fr">
        <MenuItem.Listbox bold items={[liveItem]} />
      </MenuLocaleRestricted>
      {showConfessionItem && (
        <MenuItem.Listbox
          bold
          items={[
            {
              children: t('navigation.confession-link'),
              href: getLegacyUrl('catechisme/questions-sur-les-sacrements/la-reconciliation/comment-se-confesser'),
              icon: ICON.CONFESSIO,
            },
          ]}
        />
      )}
      <MenuLocaleRestricted locale="fr">
        <MenuItem.Listbox
          bold
          items={[
            {
              children: t('app.navigation.events-link'),
              href: routes.events(),
              icon: ICON.MARKER_PLACE,
            },
            {
              children: t('app.navigation.jobs-link'),
              href: 'https://www.welcometothejungle.com/fr/companies/hozana/jobs',
              icon: ICON.WALLET,
            },
          ]}
        />
      </MenuLocaleRestricted>
      <MenuItem.Listbox
        bold
        items={[
          {
            children: t('app.navigation.footer.faq.link'),
            href: routes.faq(),
            icon: ICON.MESSAGE_QUESTION,
          },
          {
            children: t('app.i-donate'),
            onPress: () => openDonationBanner(DONATION_SOURCE.MENU),
            icon: ICON.HEART,
            classNames: { label: 'text-danger-text-contrast-default', icon: 'text-danger-text-contrast-default' },
          },
        ]}
      />
      <MenuItem.Title>{t('navigation.guides.title')}</MenuItem.Title>
      <MenuItem.Guides />
      <MenuItem.Listbox
        items={[
          { children: t('words.contact-us'), href: routes.contact() },
          { children: t('home.faq.title.head'), href: routes.introduction() },
          { children: t('app.navigation.footer.who.we.are.link'), href: routes.about() },
          { children: t('community.all'), href: routes.communitiesList() },
          { children: t('app.navigation.footer.guide.all'), href: routes.guides() },
          { children: t('words.terms-of-use'), href: routes.cgu() },
        ]}
      />
      <MenuItem.Logo />
      <MenuItem.Version />
    </ScrollView>
  )
}
